import {useQueryClient} from '@tanstack/react-query'
import {createMutation} from 'react-query-kit'

import {cancelGetAllMemexData} from '../../../api/memex/api-get-all-memex-data'
import {apiRemoveItems} from '../../../api/memex-items/api-remove-items'
import useToasts from '../../../components/toasts/use-toasts'
import {useWrapMutationMutateAsync} from '../../../helpers/mutation-helpers'
import {ApiError} from '../../../platform/api-error'
import {Resources} from '../../../strings'
import {useRemoveSuggestions} from '../../suggestions/use-remove-suggestions'
import {removeMemexItemsFromQueryClient} from '../query-client-api/memex-items/remove-items'
import {rollbackMemexItemData} from '../query-client-api/memex-items/update-items'

export const useRemoveItemsMutation = () => {
  const {removeSuggestions} = useRemoveSuggestions()
  const queryClient = useQueryClient()
  const {addToast} = useToasts()

  // We pushed these callbacks into the mutation definition (as opposed to at the call site) so that they will be called
  // even if the components that initiated the mutation are unmounted.
  // Without this approach, we would not be able to call the `onSuccess` and `onError` callbacks in conjunction with an optimistic update
  // as the component that triggered the removal might have been removed from the component hierarchy as part of the optimistic update.
  // See https://tkdodo.eu/blog/mastering-mutations-in-react-query#some-callbacks-might-not-fire for more details.
  const mutationHook = createMutation({
    mutationFn: (request: {memexProjectItemIds: Array<number>}) => {
      return apiRemoveItems(request)
    },
    onMutate: request => {
      // Optimistically remove suggestions prior to removing the items
      // Suggestion cache keys require item content type
      removeSuggestions(request.memexProjectItemIds)
      // Optimistically remove items
      const rollbackData = removeMemexItemsFromQueryClient(queryClient, request.memexProjectItemIds)
      cancelGetAllMemexData()
      return {rollbackData}
    },
    onSuccess: () => {},
    onError: (error: unknown, _request, context) => {
      if (context?.rollbackData) {
        rollbackMemexItemData(queryClient, context.rollbackData)
      }
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type: 'error', message: error instanceof ApiError ? error.message : Resources.genericErrorMessage})
    },
  })

  const mutation = mutationHook()
  return useWrapMutationMutateAsync(mutation)
}
